import { doc, onSnapshot } from 'firebase/firestore'
import { useEffect, useMemo, useState } from 'react'
import { Collections } from '../../../pure-js/libs/Collections'
import { mapQueryResponse } from '../../../pure-js/libs/FirebaseStorageClientBaseHelper'
import { Activity, ActivityStatus } from '../../../pure-js/types/GrooverTypes'
import { db } from '../libs/Firebase'
import { useMounted } from './useMounted'

const toLiveActivity = (mapped: Activity | undefined): Activity | null => {
  if (!mapped || mapped.status === ActivityStatus.Deleted) return null
  return mapped
}

export const useActivitiesByIds = (activityIds: string[]): Record<string, Activity | null> => {
  const isMounted = useMounted()
  const [data, setData] = useState<Record<string, Activity | null>>({})
  const idsKey = useMemo(() => [...new Set(activityIds.filter(Boolean))].sort().join('\0'), [activityIds])

  useEffect(() => {
    const ids = idsKey ? idsKey.split('\0') : []
    if (ids.length === 0) {
      setData({})
      return
    }

    const unsubscribes = ids.map((id) =>
      // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
      onSnapshot(
        doc(db, `${Collections.ACTIVITIES}/${id}`),
        (snap) => {
          if (!isMounted()) {
            console.warn('[useActivitiesByIds] Component unmounted, discarding snapshot')
            return
          }
          const mapped = snap.exists() ? (mapQueryResponse(snap) as Activity | undefined) : undefined
          setData((prev) => ({ ...prev, [id]: toLiveActivity(mapped) }))
        },
        (error) => {
          if (!isMounted()) {
            console.warn('[useActivitiesByIds] Component unmounted, discarding error')
            return
          }
          console.error('[useActivitiesByIds] Failed to load activity', id, error)
          setData((prev) => ({ ...prev, [id]: null }))
        }
      )
    )

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe())
  }, [idsKey, isMounted])

  return data
}
